import { formatLocaleTime, formatLocaleTimeRange } from '@hqo/date-time/core';

/**
 * The guarded way to call `@hqo/date-time`'s time formatters. **Import these rather than the package's
 * versions directly** — `format-locale-time.guard.spec.ts` enforces it.
 *
 * The package's own formatters disagree about what an unusable date means:
 *
 *   formatDate(undefined, …)            -> ''
 *   formatLocaleTime(new Date(NaN), …)  -> throws RangeError: Invalid time value
 *   formatLocaleTimeRange(…)            -> throws RangeError: Invalid time value
 *
 * That asymmetry is raised upstream as VIS-1852. Until it lands, a `formatDate` call that degrades
 * quietly sits beside a `formatLocaleTime` call that takes the view down, and the two look identical
 * at a glance.
 *
 * It matters here because the code being replaced used `Date.toLocaleString`, which returns the
 * literal string "Invalid Date" and never throws — so migrating onto the shared package converts a
 * cosmetic defect into a white screen. Content is not required to carry event times at all: an offer
 * or a plain post has none, and `start_at` can be present but unparseable. Review caught this at
 * three separate call sites in one PR, which is what prompted collecting it here.
 */
const isRealDate = (value: Date): boolean => !Number.isNaN(value.getTime());

/** The locale's own clock, or `''` for a date that cannot be formatted. Never throws. */
export const formatLocaleTimeOrEmpty = (value: Date, locale: string, timeZone?: string): string => {
  return isRealDate(value) ? formatLocaleTime(value, locale, timeZone) : '';
};

/** The locale's own time range, or `''` if either end cannot be formatted. Never throws. */
export const formatLocaleTimeRangeOrEmpty = (start: Date, end: Date, locale: string, timeZone?: string): string => {
  return isRealDate(start) && isRealDate(end) ? formatLocaleTimeRange(start, end, locale, timeZone) : '';
};
